import React, { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import {
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    ChartOptions,
    Tick,
    Filler,
} from 'chart.js';
import ChartJS from 'chart.js/auto';
import { Button } from '@mui/material';
import axios from 'axios';
import moment from 'moment';
import { ProjectConfig } from "Global";
import { ApiRequest } from 'GlobalFunctions';
import chartFakeData from './chartFakeData.json';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

const LineChart = (params: any) => {
    const { filter } = params;
    const [type, setType] = useState("users"); 
    const [data, setData] = useState<any>({});
    const [hiddenDatasets, setHiddenDatasets] = useState<Set<number>>(new Set());
    const [useFakeData, setUseFakeData] = useState(false);

    const getFakeData = () => {
        return chartFakeData[type as keyof typeof chartFakeData];
    };

    const verticalLinePlugin = {
        id: 'verticalLine',
        afterDraw: (chart: any) => {
            if (chart.tooltip._active && chart.tooltip._active.length) {
                const ctx = chart.ctx;
                const activePoint = chart.tooltip._active[0];
                const x = activePoint.element.x;
                const topY = chart.chartArea.top;
                const bottomY = chart.chartArea.bottom;
                ctx.save();
                ctx.beginPath();
                ctx.setLineDash([5, 5]);
                ctx.strokeStyle = 'rgba(0, 0, 0, 0.3)';
                ctx.lineWidth = 1;
                ctx.moveTo(x, topY);
                ctx.lineTo(x, bottomY);
                ctx.stroke();
                ctx.restore();
            }
        }
    };

    const getDashboardData = async () => {
        if (useFakeData) {
            setData(getFakeData());
            return;
        }

        await ApiRequest({
            method: 'get',
            url: "/dashboard/chartData",
            query: {
                filter,
                type
            },
            setResponse: (response: any) => {
                setData(response);
            }
        })
    };

    const createGradient = (ctx: CanvasRenderingContext2D, chartArea: any, color: string) => {
        const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.top + 100);
        const hexToRgb = (hex: string) => {
            const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
            return result ? {
                r: parseInt(result[1], 16),
                g: parseInt(result[2], 16),
                b: parseInt(result[3], 16)
            } : null;
        };
        const rgb = hexToRgb(color);
        if (rgb) {
            gradient.addColorStop(0, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.15)`);
            gradient.addColorStop(1, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0)`);
        } else {
            gradient.addColorStop(0, `rgba(121, 99, 255, 0.15)`);
            gradient.addColorStop(1, `rgba(121, 99, 255, 0)`);
        }
        return gradient;
    };

    useEffect(() => {
        getDashboardData();
    }, [filter, type, useFakeData]);

    const handleTypeClick = (newFilter: string) => {
        setType(newFilter);
        setHiddenDatasets(new Set());
    }

    const handleLegendClick = (datasetIndex: number) => {
        const newHiddenDatasets = new Set(hiddenDatasets);
        if (newHiddenDatasets.has(datasetIndex)) {
            newHiddenDatasets.delete(datasetIndex);
        } else {
            newHiddenDatasets.add(datasetIndex);
        }
        setHiddenDatasets(newHiddenDatasets);
    }

    if (!data || Object.keys(data).length === 0) {
        return null;
    }

    const filteredData = {
        ...data,
        datasets: data.datasets ? data.datasets.map((dataset: any, index: number) => ({
            ...dataset,
            hidden: hiddenDatasets.has(index),
            fill: true,
            backgroundColor: (context: any) => {
                const chart = context.chart;
                const { ctx, chartArea } = chart;
                if (!chartArea) {
                    return null;
                }
                return createGradient(ctx, chartArea, dataset.borderColor || dataset.backgroundColor);
            },
            tension: 0,
        })) : []
    };

    const getDisplayIndices = () => {
        if (!data.displayLabels || !data.labels) return [];
        const indices: number[] = [];
        if (!data.displayTimeUnit || !data.dataTimeUnit || data.displayTimeUnit === data.dataTimeUnit) {
            return data.labels.map((_: any, index: number) => index);
        }
        data.displayLabels.forEach((displayLabel: string) => {
            if (data.displayTimeUnit === 'month' && data.dataTimeUnit === 'day') {
                const targetMonth = moment(displayLabel, 'MMM YYYY');
                // Find all days that belong to this month
                const monthIndices: number[] = [];
                data.labels.forEach((label: string, index: number) => {
                    const dayMoment = moment(label, 'MMM DD, YYYY');
                    if (dayMoment.isSame(targetMonth, 'month')) {
                        monthIndices.push(index);
                    }
                });
                // Use the middle day of the month for better distribution
                if (monthIndices.length > 0) {
                    const middleIndex = Math.floor(monthIndices.length / 2);
                    indices.push(monthIndices[middleIndex]);
                }
            } else if (data.displayTimeUnit === 'year' && data.dataTimeUnit === 'month') {
                const yearStart = moment(displayLabel, 'YYYY').startOf('year');
                const monthIndex = data.labels.findIndex((label: string) => {
                    const monthMoment = moment(label, 'MMM YYYY');
                    return monthMoment.isSame(yearStart, 'year') && monthMoment.month() === 0;
                });
                if (monthIndex !== -1) indices.push(monthIndex);
            } else {
                const exactIndex = data.labels.findIndex((label: string) => label === displayLabel);
                if (exactIndex !== -1) indices.push(exactIndex);
            }
        });
        return indices;
    };

    const displayIndices = getDisplayIndices();

    const options: ChartOptions<'line'> = {
        maintainAspectRatio: false,
        responsive: true,
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                mode: 'index',
                intersect: false,
                callbacks: {
                    title: function(context: any) {
                        const index = context[0].dataIndex;
                        return data.labels[index];
                    },
                    label: function(context: any) {
                        const value = context.parsed.y;
                        const prefix = type === "revenue" ? "$" : "";
                        const datasetLabel = context.dataset.label || '';
                        if (value >= 1000) {
                            return `${datasetLabel}: ${prefix}${value / 1000}k`;
                        } else {
                            return `${datasetLabel}: ${prefix}${value}`;
                        }
                    }
                }
            }
        },
        interaction: {
            mode: 'index',
            intersect: false
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    stepSize: type === "revenue" ? 1000 : 10,
                    maxTicksLimit: 8,
                    callback: function(tickValue: string | number, index: number, ticks: Tick[]) {
                        if (typeof tickValue === 'number') {
                            const prefix = type === "revenue" ? "$" : "";
                            if (tickValue >= 1000) {
                                return prefix + tickValue / 1000 + 'k';
                            } else {
                                return prefix + tickValue;
                            }
                        }
                        return tickValue;
                    },
                },
                grid: {
                    display: true,
                    color: 'rgba(0, 0, 0, 0.1)',
                    lineWidth: 1,
                    drawTicks: false,
                    drawBorder: false
                }
            },
            x: {
                type: 'category',
                ticks: {
                    maxTicksLimit: 5,
                    autoSkip: true,   
                    maxRotation: 0,   
                    minRotation: 0,
                    callback: function(tickValue: string | number, index: number) {
                        if (data.displayLabels && data.displayTimeUnit && data.dataTimeUnit && 
                            data.displayTimeUnit !== data.dataTimeUnit) {
                            // Only show labels for our calculated display indices
                            if (displayIndices.includes(index)) {
                                const displayIndex = displayIndices.indexOf(index);
                                return data.displayLabels[displayIndex] || '';
                            }
                            return null; // Hide other labels
                        }
                        return data.labels[index] || '';
                    }
                },
                grid: {
                    display: false,
                    drawBorder: false
                }
            },
        },
    };

    return (
        <div className="planChart">
            <div className='planChart_header'>
                <div className='planChart_buttons'>
                    <Button 
                        onClick={() => handleTypeClick("users")}
                        className={type === "users" ? "active" : ""}
                        sx={{
                            '&:hover': {
                              backgroundColor: 'transparent',
                              color: '#7963ff'
                            }
                          }}
                    >
                        Users
                    </Button>
                    <Button 
                        onClick={() => handleTypeClick("revenue")}
                        className={type === "revenue" ? "active" : ""}
                        sx={{
                            '&:hover': {
                              backgroundColor: 'transparent',
                              color: '#7963ff'
                            }
                          }}
                    >
                        Monthly Recurring Revenue
                    </Button>
                </div>

                <div className='planChart_legends'>
                    {data && data.totals && data.totals.map((total: any, index: number) => (
                        <div 
                            className='planChart_legend' 
                            key={index}
                            onClick={() => handleLegendClick(index)}
                            style={{
                                cursor: 'pointer',
                                opacity: hiddenDatasets.has(index) ? 0.5 : 1,
                                textDecoration: hiddenDatasets.has(index) ? 'line-through' : 'none'
                            }}
                        >
                            <div className='planChart_value'>
                                {type === "revenue" ? "$" + total.total : total.total}
                            </div>
                            <div className='planChart_label'>
                                <div 
                                    className='planChart_legendCircle' 
                                    style={{
                                        backgroundColor: hiddenDatasets.has(index) ? '#cccccc' : total.color
                                    }}
                                ></div>
                                <span>{total.plan_name}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            
            <div style={{ height: '200px', position: 'relative' }}>
                <Line data={filteredData} options={options} plugins={[verticalLinePlugin]} />
            </div>
        </div>
    );
};

export default LineChart;
